import { humanizeError } from "@/lib/errors";
import { useCallback, useEffect, useState } from "react";
import { FileKey, KeyRound, MoreHorizontal, Plus, RotateCcw, Trash2 } from "lucide-react";

import { APIError, createSecret, deleteSecret, listSecrets, rotateSecret, type Secret } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { PageHeader, Spinner } from "@/components/Page";

const is403 = (e: unknown) => e instanceof APIError && e.status === 403;

// utf8B64 base64-encodes a (possibly multi-byte) string for the file_b64 field.
function utf8B64(s: string): string {
  return btoa(String.fromCharCode(...new TextEncoder().encode(s)));
}

export function Secrets() {
  const [secrets, setSecrets] = useState<Secret[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [rotating, setRotating] = useState<Secret | null>(null);
  const toast = useToast();
  const confirm = useConfirm();

  const refresh = useCallback(async () => {
    try {
      setSecrets(await listSecrets());
      setErr(null);
      setForbidden(false);
    } catch (e) {
      if (is403(e)) setForbidden(true);
      else setErr(humanizeError(e, "Couldn't load secrets"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onDelete = useCallback(
    async (s: Secret) => {
      const ok = await confirm({
        title: `Delete "${s.name}"?`,
        description: "This can't be undone. If it's attached to any integration, the delete is blocked until you detach it.",
        confirmText: "Delete",
        cancelText: "Cancel",
      });
      if (!ok) return;
      try {
        await deleteSecret(s.id);
        setSecrets((cur) => cur.filter((x) => x.id !== s.id));
        toast.success(`"${s.name}" deleted.`);
      } catch (e) {
        toast.error(humanizeError(e, "Delete failed"));
      }
    },
    [confirm, toast],
  );

  return (
    <div className="mx-auto w-full max-w-[1100px]">
      <PageHeader
        title="Secrets"
        description="Save a credential once — a password, API key, or a file like a Google service-account key — and use it across your integrations. You won't see the value again after saving."
        action={
          !forbidden ? (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New secret
            </Button>
          ) : undefined
        }
      />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Workspace secrets</CardTitle>
          <CardDescription>
            Add a secret here, then attach it when you connect an integration.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Spinner /> Loading…
            </div>
          ) : forbidden ? (
            <div className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
              You need editor access to manage secrets.
            </div>
          ) : err ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
              <span className="text-destructive">{err}</span>
              <Button variant="outline" size="sm" onClick={() => void refresh()}>
                Retry
              </Button>
            </div>
          ) : secrets.length === 0 ? (
            <button
              onClick={() => setCreateOpen(true)}
              className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed py-10 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/30"
            >
              <KeyRound className="h-6 w-6 opacity-50" />
              <span>
                No secrets yet. <span className="font-medium text-foreground">Add one</span> to attach to an
                integration.
              </span>
            </button>
          ) : (
            <ul className="divide-y rounded-md border">
              {secrets.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    {s.kind === "file" ? (
                      <FileKey className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{s.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.kind === "file" ? "file" : "value"} · {s.byte_len} bytes · created{" "}
                        {new Date(s.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label="Secret actions">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => setRotating(s)}>
                        <RotateCcw /> Rotate
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => void onDelete(s)} className="text-destructive focus:text-destructive">
                        <Trash2 /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <CreateSecretDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(s) => {
          setSecrets((cur) => [s, ...cur]);
          setCreateOpen(false);
        }}
      />

      <RotateSecretDialog
        secret={rotating}
        onClose={() => setRotating(null)}
        onRotated={(updated) => {
          setSecrets((cur) => cur.map((x) => (x.id === updated.id ? updated : x)));
          setRotating(null);
        }}
      />
    </div>
  );
}

function RotateSecretDialog({
  secret,
  onClose,
  onRotated,
}: {
  secret: Secret | null;
  onClose: () => void;
  onRotated: (s: Secret) => void;
}) {
  const [value, setValue] = useState("");
  const [fileText, setFileText] = useState("");
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setValue("");
    setFileText("");
    setFileName(secret?.file_name ?? "");
  }, [secret]);

  if (!secret) return null;
  const isFile = secret.kind === "file";
  const canSubmit = isFile ? fileText !== "" : value !== "";

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      const body = isFile
        ? { file_b64: utf8B64(fileText), file_name: fileName.trim() || undefined }
        : { value };
      const res = await rotateSecret(secret.id, body);
      const names = res.restarted_servers ?? [];
      toast.success(names.length ? `Rotated — restarting ${names.join(", ")}` : "Rotated.");
      onRotated(res.secret);
    } catch (e) {
      toast.error(humanizeError(e, "Couldn't rotate the secret"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!secret} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Rotate "{secret.name}"</DialogTitle>
          <DialogDescription>
            {isFile ? "Upload the new file" : "Enter the new value"}. The old one stops working immediately, and any
            integration using this secret restarts to pick up the new one.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isFile ? (
            <>
              <div>
                <label className="mb-1 block text-sm font-medium">New file contents</label>
                <textarea
                  value={fileText}
                  onChange={(e) => setFileText(e.target.value)}
                  rows={6}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder={'paste the new file contents, e.g. {"type":"service_account",...}'}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">File name (optional)</label>
                <Input
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  placeholder="service-account.json"
                  maxLength={120}
                />
              </div>
            </>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium">New value</label>
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="paste the new value"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit || saving}>
            {saving ? <Spinner className="h-4 w-4" /> : "Rotate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateSecretDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (s: Secret) => void;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"value" | "file">("file");
  const [value, setValue] = useState("");
  const [fileText, setFileText] = useState("");
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const reset = () => {
    setName("");
    setKind("file");
    setValue("");
    setFileText("");
    setFileName("");
  };

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      const body =
        kind === "value"
          ? { name: name.trim(), kind: "value" as const, value }
          : { name: name.trim(), kind: "file" as const, file_b64: utf8B64(fileText), file_name: fileName.trim() || undefined };
      const s = await createSecret(body);
      toast.success(`Secret "${s.name}" saved.`);
      reset();
      onCreated(s);
    } catch (e) {
      toast.error(humanizeError(e, "Couldn't save the secret"));
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = name.trim() !== "" && (kind === "value" ? value !== "" : fileText !== "");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && (reset(), onClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New secret</DialogTitle>
          <DialogDescription>You won't be able to view this again after saving, so keep a copy if you need one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ga-service-account" maxLength={120} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Type</label>
            <div className="flex gap-2">
              {(["file", "value"] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={
                    "flex-1 rounded-md border px-3 py-2 text-sm transition-colors " +
                    (kind === k ? "border-primary bg-primary/10 text-primary" : "border-input hover:bg-accent")
                  }
                >
                  {k === "file" ? "File" : "Value"}
                </button>
              ))}
            </div>
          </div>
          {kind === "value" ? (
            <div>
              <label className="mb-1 block text-sm font-medium">Value</label>
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="paste the secret value"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-sm font-medium">File contents</label>
                <textarea
                  value={fileText}
                  onChange={(e) => setFileText(e.target.value)}
                  rows={6}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder='paste the file contents, e.g. {"type":"service_account",...}'
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">File name (optional)</label>
                <Input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="service-account.json" maxLength={120} />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => (reset(), onClose())} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit || saving}>
            {saving ? <Spinner className="h-4 w-4" /> : "Save secret"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
